import {
  Badge,
  Box,
  Button,
  Card,
  Divider,
  FileButton,
  Flex,
  Grid,
  Group,
  Loader,
  MantineProvider,
  Modal,
  NumberInput,
  Paper,
  ScrollArea,
  Select,
  Stack,
  Text,
  TextInput,
  Title,
  UnstyledButton,
} from "@mantine/core";
import {
  IconArrowUp,
  IconChevronDown,
  IconChevronRight,
  IconEdit,
  IconEye,
  IconFileText,
  IconPlus,
  IconTrash,
} from "@tabler/icons-react";
import { renderAsync } from "docx-preview";
import { useCallback, useEffect, useRef, useState } from "react";
import { NotificationExtension } from "../../../_base/extension/NotificationExtension";
import { api } from "../../../library/axios";
import BreadCrumb from "../../../_base/component/_layout/_breadcrumb";
import { AxiosResponse } from "axios";

interface ChuyenMuc {
  idChuyenMuc: number;
  tenChuyenMuc: string;
  idCha: number;
  thuTu: number;
}

interface TaiLieu {
  idDocument: number;
  tenTaiLieu: string;
  idChuyenMuc: number;
  tepTaiLieu: string;
  phatHanh: string;
  lastUpdateDate: string;
}

interface CayThuMuc {
  ten: string;
  children?: CayThuMuc[];
}

interface FormThemChuyenMuc {
  tenChuyenMuc: string;
  idCha: number;
  thuTu: number;
}
interface ModalThemTaiLieuProps {
  opened: boolean;
  onClose: () => void;
  dataChuyenMuc: ChuyenMuc[];
  onSuccess: () => void;
}

export const ModalThemTaiLieu = ({
  opened,
  onClose,
  dataChuyenMuc,
  onSuccess,
}: ModalThemTaiLieuProps) => {
  const [tenTaiLieu, setTenTaiLieu] = useState("");
  const [tepTaiLieu, setTepTaiLieu] = useState("");
  const [idChuyenMuc, setIdChuyenMuc] = useState<string | null>("0");
  const [phatHanh, setPhatHanh] = useState<"Y" | "N">("N");
  const [dangGui, setDangGui] = useState(false);
  const [loi, setLoi] = useState<{ [key: string]: string }>({});
  const [file, setFile] = useState<File | null>(null);
  console.log("dataChuyenMuc", dataChuyenMuc);
  const convertFileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result?.toString().split(",")[1] || "";
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const kiemTraForm = () => {
    const _loi: { [key: string]: string } = {};
    if (!tenTaiLieu.trim())
      _loi.tenTaiLieu = "Tên tài liệu không được để trống";
    if (!idChuyenMuc) _loi.idChuyenMuc = "Vui lòng chọn chuyên mục";
    setLoi(_loi);
    return Object.keys(_loi).length === 0;
  };

  const handleSubmit = async () => {
    if (!kiemTraForm()) return;

    try {
      setDangGui(true);
      await api.post("/api/v1/Document/create", {
        tenTaiLieu: tenTaiLieu.trim(),
        tepTaiLieu,
        idChuyenMuc: parseInt(idChuyenMuc || "0"),
        phatHanh,
      });

      NotificationExtension.Success("Thêm tài liệu thành công!");
      onSuccess();
      onClose();
      resetForm();
    } catch (err: any) {
      NotificationExtension.Fails(
        "Lỗi khi thêm tài liệu: " + (err.response?.data?.message || err.message)
      );
    } finally {
      setDangGui(false);
    }
  };

  const resetForm = () => {
    setTenTaiLieu("");
    setTepTaiLieu("");
    setIdChuyenMuc("0");
    setPhatHanh("N");
    setFile(null);
    setLoi({});
  };

  useEffect(() => {
    if (!opened) {
      resetForm();
    }
  }, [opened]);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="📄 Thêm tài liệu mới"
      size="md"
    >
      <Box>
        <TextInput
          label="Tên tài liệu"
          placeholder="Nhập tên tài liệu"
          value={tenTaiLieu}
          onChange={(e) => setTenTaiLieu(e.currentTarget.value)}
          error={loi.tenTaiLieu}
          required
        />

        <TextInput
          label="Đính kèm"
          placeholder="Chọn file .docx nhỏ hơn 1MB"
          value={file?.name || ""}
          readOnly
          mt="md"
          rightSection={
            <FileButton
              onChange={async (selectedFile) => {
                if (!selectedFile) return;

                if (
                  selectedFile.type !==
                    "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
                  selectedFile.size > 1024 * 1024
                ) {
                  NotificationExtension.Fails(
                    "Chỉ chấp nhận file .docx nhỏ hơn 1MB!"
                  );
                  return;
                }

                try {
                  const base64String = await convertFileToBase64(selectedFile);
                  setFile(selectedFile);
                  setTepTaiLieu(base64String);
                } catch (error) {
                  NotificationExtension.Fails("Lỗi khi xử lý file!");
                }
              }}
              accept=".docx"
            >
              {(props) => (
                <Button
                  {...props}
                  size="xs"
                  variant="light"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <IconArrowUp size={16} />
                </Button>
              )}
            </FileButton>
          }
        />

        {file && (
          <Text size="xs" c="dimmed" mt="xs">
            File đã chọn: {file.name} ({Math.round(file.size / 1024)}KB)
          </Text>
        )}

        <Select
          label="Chuyên mục"
          placeholder="Chọn chuyên mục"
          mt="md"
          data={[
            { value: "0", label: "📁 Thư mục gốc" },
            ...dataChuyenMuc?.map((cm) => ({
              value: cm.idChuyenMuc.toString(),
              label: `📂 ${cm.tenChuyenMuc}`,
            })),
          ]}
          value={idChuyenMuc}
          onChange={setIdChuyenMuc}
          error={loi.idChuyenMuc}
          searchable
          required
        />

        <Select
          label="Trạng thái phát hành"
          mt="md"
          value={phatHanh}
          onChange={(value) => setPhatHanh(value as "Y" | "N")}
          data={[
            { value: "Y", label: "✅ Đã phát hành" },
            { value: "N", label: "🚫 Chưa phát hành" },
          ]}
        />

        <Group justify="flex-end" mt="lg">
          <Button variant="outline" onClick={onClose} disabled={dangGui}>
            Hủy
          </Button>
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={handleSubmit}
            loading={dangGui}
          >
            Thêm mới
          </Button>
        </Group>
      </Box>
    </Modal>
  );
};
const xayDungCayThuMuc = (data: ChuyenMuc[], idCha = 0): CayThuMuc[] => {
  return data
    .filter((item) => item.idCha === idCha)
    .sort(
      (a, b) =>
        a.thuTu - b.thuTu || a.tenChuyenMuc.localeCompare(b.tenChuyenMuc)
    )
    .map((item) => {
      const children = xayDungCayThuMuc(data, item.idChuyenMuc);
      return {
        ten: item.tenChuyenMuc,
        ...(children.length > 0 && { children }),
      };
    });
};

const dinhDangNgay = (ngayString: string) => {
  return new Date(ngayString).toLocaleDateString("vi-VN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const ChonChuyenMuc = ({
  data,
  idDaChon,
  onChon,
}: {
  data: ChuyenMuc[];
  idDaChon: number;
  onChon: (id: number, ten: string) => void;
}) => {
  const [nodeMo, setNodeMo] = useState<Set<number>>(new Set([0]));

  const chuyenDoiTrangThaiMo = (id: number) => {
    const nodeMoMoi = new Set(nodeMo);
    nodeMoMoi.has(id) ? nodeMoMoi.delete(id) : nodeMoMoi.add(id);
    setNodeMo(nodeMoMoi);
  };

  const renderNode = (idCha: number, level = 0) => {
    const children = data
      .filter((item) => item.idCha === idCha)
      .sort(
        (a, b) =>
          a.thuTu - b.thuTu || a.tenChuyenMuc.localeCompare(b.tenChuyenMuc)
      );

    return children.map((item) => {
      const coChildren = data.some((child) => child.idCha === item.idChuyenMuc);
      const daMo = nodeMo.has(item.idChuyenMuc);
      const daChon = idDaChon === item.idChuyenMuc;

      return (
        <Box key={item.idChuyenMuc}>
          <UnstyledButton
            onClick={() => onChon(item.idChuyenMuc, item.tenChuyenMuc)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              padding: "4px 8px",
              marginLeft: level * 20,
              width: `calc(100% - ${level * 20}px)`,
              backgroundColor: daChon ? "#e7f5ff" : "transparent",
              borderRadius: 4,
              border: daChon ? "1px solid #339af0" : "1px solid transparent",
            }}
          >
            <Box
              onClick={(e) => {
                e.stopPropagation();
                if (coChildren) chuyenDoiTrangThaiMo(item.idChuyenMuc);
              }}
              style={{ width: 16, cursor: coChildren ? "pointer" : "default" }}
            >
              {coChildren ? (
                daMo ? (
                  <IconChevronDown size={16} />
                ) : (
                  <IconChevronRight size={16} />
                )
              ) : (
                <span style={{ width: 16 }} />
              )}
            </Box>
            <Text size="sm" style={{ flex: 1 }}>
              📂 {item.tenChuyenMuc}
            </Text>
          </UnstyledButton>

          {coChildren && daMo && (
            <Box>{renderNode(item.idChuyenMuc, level + 1)}</Box>
          )}
        </Box>
      );
    });
  };
  return (
    <Box>
      {/* Thêm nút chọn thư mục gốc nổi bật hơn */}
      <UnstyledButton
        onClick={() => onChon(0, "Thư mục gốc")}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "8px 12px",
          width: "100%",
          backgroundColor: idDaChon === 0 ? "#e7f5ff" : "#f8f9fa",
          borderRadius: 4,
          border: idDaChon === 0 ? "1px solid #339af0" : "1px solid #dee2e6",
          marginBottom: 8,
          fontWeight: idDaChon === 0 ? 500 : 400,
        }}
      >
        <Text size="sm" style={{ flex: 1 }}>
          🏠 Thư mục gốc (cấp cao nhất)
        </Text>
      </UnstyledButton>

      <Divider my="xs" />
      {renderNode(0)}
    </Box>
  );
};

const NodeThuMuc = ({
  item,
  onEdit,
  onDelete,
  onSelect,
  dataChuyenMuc,
  idChuyenMucDaChon,
}: {
  item: CayThuMuc;
  onEdit: (chuyenMuc: ChuyenMuc) => void;
  onDelete: (chuyenMuc: ChuyenMuc) => void;
  onSelect: (idChuyenMuc: number) => void;
  dataChuyenMuc: ChuyenMuc[];
  idChuyenMucDaChon: number;
}) => {
  const [daMo, setDaMo] = useState(!!item.children);
  const chuyenMuc = dataChuyenMuc?.find((c) => c.tenChuyenMuc === item.ten);
  const daChon = chuyenMuc && chuyenMuc.idChuyenMuc === idChuyenMucDaChon;

  return (
    <Box pl="sm">
      <Group gap="xs" style={{ alignItems: "center" }}>
        <UnstyledButton
          onClick={() => {
            if (item.children) setDaMo((o) => !o);
            if (chuyenMuc) onSelect(chuyenMuc.idChuyenMuc);
          }}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 4,
            flex: 1,
            backgroundColor: daChon ? "#e7f5ff" : "transparent",
            padding: "4px 8px",
            borderRadius: 4,
            border: daChon ? "1px solid #339af0" : "1px solid transparent",
          }}
        >
          {item.children ? (
            daMo ? (
              <IconChevronDown size={16} />
            ) : (
              <IconChevronRight size={16} />
            )
          ) : (
            <span style={{ width: 16 }} />
          )}
          <Text size="sm">📂 {item.ten}</Text>
        </UnstyledButton>

        {chuyenMuc && (
          <Group gap={2}>
            <UnstyledButton
              onClick={() => onEdit(chuyenMuc)}
              style={{
                padding: 4,
                borderRadius: 4,
                color: "#fab005",
                display: "flex",
                alignItems: "center",
              }}
            >
              <IconEdit size={14} />
            </UnstyledButton>
            <UnstyledButton
              onClick={() => onDelete(chuyenMuc)}
              style={{
                padding: 4,
                borderRadius: 4,
                color: "#fa5252",
                display: "flex",
                alignItems: "center",
              }}
            >
              <IconTrash size={14} />
            </UnstyledButton>
          </Group>
        )}
      </Group>

      {daMo && item.children && (
        <Box pl="md">
          {item.children.map((child, idx) => (
            <NodeThuMuc
              key={idx}
              item={child}
              onEdit={onEdit}
              onDelete={onDelete}
              onSelect={onSelect}
              dataChuyenMuc={dataChuyenMuc}
              idChuyenMucDaChon={idChuyenMucDaChon}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

const CardTaiLieu = ({
  taiLieu,
  tenChuyenMuc,
  onXoa,
  onViewFile,
}: {
  taiLieu: TaiLieu;
  tenChuyenMuc: string;
  onXoa: (id: number) => void;
  onViewFile: (taiLieu: TaiLieu) => void;
}) => (
  <Card shadow="sm" padding="md" radius="md" withBorder>
    <Group gap="sm" align="flex-start">
      <Box style={{ color: "#1c7ed6" }}>
        <IconFileText size={24} />
      </Box>
      <Box style={{ flex: 1 }}>
        <Flex justify="space-between" mb="xs">
          <Text fw={500} size="sm" lineClamp={2}>
            {taiLieu.tenTaiLieu}
          </Text>
          <Group>
            <IconEye
              size={16}
              onClick={() => onViewFile(taiLieu)}
              style={{ marginLeft: 8, cursor: "pointer", color: "#1c7ed6" }}
            />
            <IconTrash
              size={16}
              style={{ marginLeft: 8, cursor: "pointer", color: "#fa5252" }}
              onClick={() => onXoa(taiLieu.idDocument)}
            />
          </Group>
        </Flex>
        <Text size="xs" c="dimmed" mt="xs">
          📂 {tenChuyenMuc}
        </Text>

        <Group gap="xs" mt="sm">
          <Badge
            size="xs"
            variant="light"
            color={taiLieu.phatHanh === "Y" ? "green" : "gray"}
          >
            {taiLieu.phatHanh === "Y" ? "Đã phát hành" : "Chưa phát hành"}
          </Badge>
          <Text size="xs" c="dimmed">
            {dinhDangNgay(taiLieu.lastUpdateDate)}
          </Text>
        </Group>
      </Box>
    </Group>
  </Card>
);

export default function DocumentList() {
  const [moModalTaiLieu, setMoModalTaiLieu] = useState(false);
  const [modeTaiLieu, setModeTaiLieu] = useState<"create" | "edit">("create");
  const [taiLieuDangSua, setTaiLieuDangSua] = useState<TaiLieu | null>(null);
  const [dangGuiTaiLieu, setDangGuiTaiLieu] = useState(false);
  const [selectedBase64, setSelectedBase64] = useState<string>("");
  const [selectedFileName, setSelectedFileName] = useState<string>("");
  const [cayThuMuc, setCayThuMuc] = useState<CayThuMuc[]>([]);
  const [dataChuyenMuc, setDataChuyenMuc] = useState<ChuyenMuc[]>([]);
  const [dataTaiLieu, setDataTaiLieu] = useState<TaiLieu[]>([]);
  const [taiLieuDaLoc, setTaiLieuDaLoc] = useState<TaiLieu[]>([]);
  const [idChuyenMucDaChon, setIdChuyenMucDaChon] = useState<number>(0);
  const [tenChuyenMucDaChon, setTenChuyenMucDaChon] =
    useState<string>("Tất cả tài liệu");
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingTaiLieu, setIsLoadingTaiLieu] = useState(false);
  const [moModal, setMoModal] = useState(false);
  const [mode, setMode] = useState<"create" | "edit">("create");
  const [chuyenMucDangSua, setChuyenMucDangSua] = useState<ChuyenMuc | null>(
    null
  );
  const [dangGui, setDangGui] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const docxContainerRef = useRef<HTMLDivElement | null>(null);

  const handleViewFile = async (row: TaiLieu) => {
    if (!row.tepTaiLieu) {
      NotificationExtension.Fails("Không có file đính kèm!");
      return;
    }
    setSelectedFileName(row.tenTaiLieu);
    setSelectedBase64(row.tepTaiLieu.trim());
    setIsModalOpen(true);
  };
  const handleDownload = () => {
    if (!selectedBase64) return;

    const link = document.createElement("a");
    link.href = `data:application/vnd.openxmlformats-officedocument.wordprocessingml.document;base64,${selectedBase64}`;
    link.download = selectedFileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  const previewContainerRef = useCallback(
    async (node: HTMLDivElement | null) => {
      if (node && selectedBase64) {
        node.innerHTML = "";
        try {
          const byteCharacters = atob(selectedBase64);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          const blob = new Blob([byteArray], {
            type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
          });

          docxContainerRef.current = node;
          await renderAsync(
            byteArray.buffer,
            docxContainerRef.current,
            undefined,
            {
              className: "docx",
              inWrapper: false,
              ignoreWidth: false,
              ignoreHeight: false,
              ignoreFonts: false,
              breakPages: true,
              ignoreLastRenderedPageBreak: true,
              experimental: false,
              trimXmlDeclaration: true,
            }
          );
        } catch (error) {
          node.innerText =
            "Lỗi: Dữ liệu file bị hỏng hoặc không đúng định dạng.";
        }
      }
    },
    [selectedBase64]
  );

  const [duLieuForm, setDuLieuForm] = useState<FormThemChuyenMuc>({
    tenChuyenMuc: "",
    idCha: 0,
    thuTu: 1,
  });
  const [tenChuyenMucChaDaChon, setTenChuyenMucChaDaChon] =
    useState("Thư mục gốc");
  const [loi, setLoi] = useState<{ [key: string]: string }>({});

  const taiDuLieuChuyenMuc = useCallback(async () => {
    try {
      setIsLoading(true);
      const response: AxiosResponse = await api.get(
        "/TblChuyenMuc/get-list?Skip=0&Take=50"
      );
      if (response && response.data?.success) {
        const data = response.data.data || [];
        setDataChuyenMuc(data);
        setCayThuMuc(xayDungCayThuMuc(data));
      }
    } catch (err: any) {
      NotificationExtension.Fails(
        "Lỗi khi tải dữ liệu chuyên mục: " + err.message
      );
    } finally {
      setIsLoading(false);
    }
  }, []);

  const taiDanhSachTaiLieu = useCallback(async () => {
    try {
      setIsLoadingTaiLieu(true);
      const res = await api.get("/api/v1/Document/get-all");
      const data = res.data || [];
      setDataTaiLieu(data);
      locTaiLieuTheoChuyenMuc(data, idChuyenMucDaChon);
    } catch (err: any) {
      NotificationExtension.Fails(
        "Lỗi khi tải dữ liệu tài liệu: " + err.message
      );
    } finally {
      setIsLoadingTaiLieu(false);
    }
  }, [idChuyenMucDaChon]);

  const locTaiLieuTheoChuyenMuc = useCallback(
    (taiLieu: TaiLieu[], idChuyenMuc: number) => {
      const ketQua =
        idChuyenMuc === 0
          ? taiLieu
          : taiLieu.filter((doc) => doc.idChuyenMuc === idChuyenMuc);
      setTaiLieuDaLoc(ketQua);
    },
    []
  );

  const chonChuyenMuc = useCallback(
    (idChuyenMuc: number) => {
      setIdChuyenMucDaChon(idChuyenMuc);
      const chuyenMuc = dataChuyenMuc.find(
        (c) => c.idChuyenMuc === idChuyenMuc
      );
      setTenChuyenMucDaChon(
        chuyenMuc ? chuyenMuc.tenChuyenMuc : "Tất cả tài liệu"
      );
      locTaiLieuTheoChuyenMuc(dataTaiLieu, idChuyenMuc);
    },
    [dataChuyenMuc, dataTaiLieu, locTaiLieuTheoChuyenMuc]
  );

  const layTenChuyenMuc = useCallback(
    (idChuyenMuc: number): string => {
      const chuyenMuc = dataChuyenMuc.find(
        (c) => c.idChuyenMuc === idChuyenMuc
      );
      return chuyenMuc ? chuyenMuc.tenChuyenMuc : "Không xác định";
    },
    [dataChuyenMuc]
  );

  const xuLyTao = () => {
    setMode("create");
    setMoModal(true);
    setDuLieuForm({ tenChuyenMuc: "", idCha: 0, thuTu: 1 });
    setTenChuyenMucChaDaChon("Thư mục gốc");
    setChuyenMucDangSua(null);
    setLoi({});
  };

  const xuLySua = (chuyenMuc: ChuyenMuc) => {
    setMode("edit");
    setMoModal(true);
    setChuyenMucDangSua(chuyenMuc);
    setDuLieuForm({
      tenChuyenMuc: chuyenMuc.tenChuyenMuc,
      idCha: chuyenMuc.idCha,
      thuTu: chuyenMuc.thuTu,
    });

    const chuyenMucCha = dataChuyenMuc.find(
      (c) => c.idChuyenMuc === chuyenMuc.idCha
    );
    setTenChuyenMucChaDaChon(
      chuyenMucCha ? chuyenMucCha.tenChuyenMuc : "Thư mục gốc"
    );
    setLoi({});
  };

  const xuLyXoaChuyenMuc = async (chuyenMuc: ChuyenMuc) => {
    const coChuyenMucCon = dataChuyenMuc.some(
      (c) => c.idCha === chuyenMuc.idChuyenMuc
    );

    if (coChuyenMucCon) {
      NotificationExtension.Fails(
        "Không thể xóa chuyên mục có chuyên mục con. Vui lòng xóa các chuyên mục con trước."
      );
      return;
    }

    if (
      !window.confirm(
        `Bạn có chắc chắn muốn xóa chuyên mục "${chuyenMuc.tenChuyenMuc}"?`
      )
    ) {
      return;
    }

    try {
      await api.post("/api/v1/ChuyenMuc/delete", [chuyenMuc.idChuyenMuc]);
      NotificationExtension.Success("Xóa chuyên mục thành công!");
      taiDuLieuChuyenMuc();
    } catch (err: any) {
      NotificationExtension.Fails(
        "Lỗi khi xóa chuyên mục: " +
          (err.response?.data?.message || err.message)
      );
    }
  };

  const xuLyXoaTaiLieu = async (idTaiLieu: number) => {
    const taiLieu = dataTaiLieu.find((doc) => doc.idDocument === idTaiLieu);
    if (!taiLieu) return;

    if (
      !window.confirm(
        ` Bạn có chắc chắn muốn xóa tài liệu "${taiLieu.tenTaiLieu}"?`
      )
    ) {
      return;
    }

    try {
      await api.post("/api/v1/Document/delete", [idTaiLieu]);
      NotificationExtension.Success(
        `Xóa tài liệu "${taiLieu.tenTaiLieu}" thành công`
      );
      taiDanhSachTaiLieu();
    } catch (err: any) {
      NotificationExtension.Fails("Lỗi khi xóa tài liệu: " + err.message);
    }
  };

  const kiemTraForm = (): boolean => {
    const loiMoi: { [key: string]: string } = {};

    if (!duLieuForm.tenChuyenMuc.trim()) {
      loiMoi.tenChuyenMuc = "Tên chuyên mục không được để trống";
    }

    if (duLieuForm.thuTu < 0) {
      loiMoi.thuTu = "Thứ tự phải lớn hơn hoặc bằng 0";
    }

    if (mode === "edit" && chuyenMucDangSua) {
      if (duLieuForm.idCha === chuyenMucDangSua.idChuyenMuc) {
        loiMoi.idCha = "Không thể chọn chính nó làm chuyên mục cha";
      }

      const laChuyenMucCon = (idCha: number, idHienTai: number): boolean => {
        const cha = dataChuyenMuc.find((c) => c.idChuyenMuc === idCha);
        if (!cha) return false;
        if (cha.idCha === idHienTai) return true;
        return laChuyenMucCon(cha.idCha, idHienTai);
      };

      if (
        duLieuForm.idCha !== 0 &&
        laChuyenMucCon(duLieuForm.idCha, chuyenMucDangSua.idChuyenMuc)
      ) {
        loiMoi.idCha = "Không thể chọn chuyên mục con làm chuyên mục cha";
      }
    }

    setLoi(loiMoi);
    return Object.keys(loiMoi).length === 0;
  };

  const xuLyGui = async () => {
    if (!kiemTraForm()) return;

    setDangGui(true);

    const payload = {
      tenChuyenMuc: duLieuForm.tenChuyenMuc.trim(),
      idCha: duLieuForm.idCha,
      thuTu: duLieuForm.thuTu,
      moTa: "string",
      attribute1: "string",
      attribute2: "string",
      attribute3: "string",
      attribute4: "string",
      attribute5: "string",
      attribute6: "string",
      attribute7: "string",
      attribute8: "string",
      attribute9: "string",
      attribute10: "string",
      attribute11: "string",
      attribute12: "string",
      attribute13: "string",
      attribute14: "string",
      attribute15: "string",
    };

    try {
      if (mode === "create") {
        await api.post("/TblChuyenMuc/create", payload);
        NotificationExtension.Success("Thêm chuyên mục thành công!");
      } else if (mode === "edit" && chuyenMucDangSua) {
        await api.post("/api/v1/ChuyenMuc/update", {
          idChuyenMuc: chuyenMucDangSua.idChuyenMuc,
          ...payload,
        });
        NotificationExtension.Success("Cập nhật chuyên mục thành công!");
      }

      setMoModal(false);
      taiDuLieuChuyenMuc();
    } catch (err: any) {
      NotificationExtension.Fails(
        "Lỗi: " + (err.response?.data?.message || err.message)
      );
    } finally {
      setDangGui(false);
    }
  };

  const chonChuyenMucCha = (id: number, ten: string) => {
    setDuLieuForm((prev) => ({ ...prev, idCha: id }));
    setTenChuyenMucChaDaChon(ten);
  };

  useEffect(() => {
    taiDuLieuChuyenMuc();
    taiDanhSachTaiLieu();
  }, [taiDuLieuChuyenMuc, taiDanhSachTaiLieu]);

  useEffect(() => {
    locTaiLieuTheoChuyenMuc(dataTaiLieu, idChuyenMucDaChon);
  }, [idChuyenMucDaChon, dataTaiLieu, locTaiLieuTheoChuyenMuc]);

  return (
    <>
      <Group justify="space-between">
        <BreadCrumb></BreadCrumb>
      </Group>

      <MantineProvider>
        <Grid>
          <Grid.Col span={4}>
            <Paper
              p="md"
              radius="md"
              shadow="sm"
              style={{ height: "80vh", overflowY: "auto" }}
            >
              <Flex justify="space-between" align="center" mb="md">
                <Title order={5} c="blue">
                  ⭐ Tài liệu yêu thích
                </Title>
                <Button
                  size="xs"
                  color="yellow"
                  variant="outline"
                  leftSection={<IconPlus size={14} />}
                  onClick={xuLyTao}
                >
                  Thêm chuyên mục
                </Button>
              </Flex>

              <UnstyledButton
                onClick={() => chonChuyenMuc(0)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "8px 12px",
                  width: "100%",
                  backgroundColor:
                    idChuyenMucDaChon === 0 ? "#e7f5ff" : "transparent",
                  borderRadius: 4,
                  border:
                    idChuyenMucDaChon === 0
                      ? "1px solid #339af0"
                      : "1px solid transparent",
                  marginBottom: 8,
                }}
              >
                <Text size="sm" fw={500}>
                  📄 Tất cả tài liệu
                </Text>
              </UnstyledButton>

              <Divider my="xs" />

              {isLoading ? (
                <Flex justify="center" align="center" h={200}>
                  <Loader size="md" />
                </Flex>
              ) : (
                cayThuMuc.map((item, idx) => (
                  <NodeThuMuc
                    key={idx}
                    item={item}
                    onEdit={xuLySua}
                    onDelete={xuLyXoaChuyenMuc}
                    onSelect={chonChuyenMuc}
                    dataChuyenMuc={dataChuyenMuc}
                    idChuyenMucDaChon={idChuyenMucDaChon}
                  />
                ))
              )}
            </Paper>
          </Grid.Col>

          <Grid.Col span={8}>
            <ScrollArea h="80vh" type="scroll" scrollbarSize={8}>
              <Paper
                p="md"
                radius="md"
                shadow="sm"
                style={{ minHeight: "100vh" }}
              >
                <Flex justify="space-between" align="center" mb="md">
                  <Box>
                    <Title order={4} mb="xs">
                      {tenChuyenMucDaChon}
                    </Title>
                    <Text size="sm" c="dimmed">
                      {taiLieuDaLoc.length} tài liệu
                    </Text>
                  </Box>
                  <Group>
                    <Button
                      variant="outline"
                      leftSection={<IconPlus size={16} />}
                      onClick={() => setMoModalTaiLieu(true)}
                    >
                      Thêm tài liệu
                    </Button>

                    <ModalThemTaiLieu
                      opened={moModalTaiLieu}
                      onClose={() => setMoModalTaiLieu(false)}
                      dataChuyenMuc={dataChuyenMuc}
                      onSuccess={taiDanhSachTaiLieu}
                    />
                  </Group>
                </Flex>

                {isLoadingTaiLieu ? (
                  <Flex justify="center" align="center" h={400}>
                    <Loader size="lg" />
                  </Flex>
                ) : taiLieuDaLoc.length === 0 ? (
                  <Flex
                    justify="center"
                    align="center"
                    h={400}
                    direction="column"
                  >
                    <IconFileText size={48} color="#ced4da" />
                    <Text size="lg" c="dimmed" mt="md">
                      Không có tài liệu nào
                    </Text>
                    <Text size="sm" c="dimmed">
                      {idChuyenMucDaChon === 0
                        ? "Chưa có tài liệu nào trong hệ thống"
                        : `Chưa có tài liệu nào trong chuyên mục "${tenChuyenMucDaChon}"`}
                    </Text>
                  </Flex>
                ) : (
                  <Stack gap="md">
                    {taiLieuDaLoc.map((doc) => (
                      <CardTaiLieu
                        key={doc.idDocument}
                        taiLieu={doc}
                        tenChuyenMuc={layTenChuyenMuc(doc.idChuyenMuc)}
                        onXoa={xuLyXoaTaiLieu}
                        onViewFile={() => handleViewFile(doc)}
                      />
                    ))}
                  </Stack>
                )}
              </Paper>
            </ScrollArea>
          </Grid.Col>
        </Grid>
        <Modal
          opened={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          title="📄 Xem nội dung tài liệu"
          size="full"
          styles={{
            body: {
              maxHeight: "70vh",
              overflowY: "auto",
              position: "relative",
            },
          }}
        >
          {selectedBase64 ? (
            <ScrollArea>
              <Box
                ref={previewContainerRef}
                style={{
                  border: "1px solid #dee2e6",

                  backgroundColor: "white",
                  minHeight: "400px",
                }}
              />
            </ScrollArea>
          ) : (
            <Text c="dimmed">Không có dữ liệu để hiển thị</Text>
          )}
          <Button
            mt="md"
            fullWidth
            color="blue"
            onClick={handleDownload}
            disabled={!selectedBase64}
          >
            Tải xuống Tài liệu ({selectedFileName})
          </Button>
        </Modal>
        <Modal
          opened={moModal}
          onClose={() => setMoModal(false)}
          title={
            mode === "create"
              ? "📁 Thêm chuyên mục mới"
              : "✏️ Cập nhật chuyên mục"
          }
          size="md"
        >
          <Stack>
            <TextInput
              label="Tên chuyên mục"
              placeholder="Nhập tên chuyên mục"
              value={duLieuForm.tenChuyenMuc}
              onChange={(e) =>
                setDuLieuForm((prev) => ({
                  ...prev,
                  tenChuyenMuc: e.currentTarget.value,
                }))
              }
              error={loi.tenChuyenMuc}
              required
            />

            <Box>
              <Text size="sm" mb="xs" fw={500}>
                Chuyên mục cha:{" "}
                <Text span c="blue">
                  {tenChuyenMucChaDaChon}
                </Text>
              </Text>
              <Box
                style={{
                  maxHeight: 200,
                  overflowY: "auto",
                  border: "1px solid #dee2e6",
                  borderRadius: 8,
                  padding: 8,
                }}
              >
                <ChonChuyenMuc
                  data={dataChuyenMuc}
                  idDaChon={duLieuForm.idCha}
                  onChon={chonChuyenMucCha}
                />
              </Box>
              {loi.idCha && (
                <Text size="xs" c="red" mt="xs">
                  {loi.idCha}
                </Text>
              )}
            </Box>
            <NumberInput
              label="Thứ tự"
              min={0}
              value={duLieuForm.thuTu}
              onChange={(val) =>
                setDuLieuForm((prev) => ({
                  ...prev,
                  thuTu: typeof val === "number" ? val : 0,
                }))
              }
              error={loi.thuTu}
              required
            />
            <Group justify="flex-end" mt="md">
              <Button
                variant="outline"
                onClick={() => setMoModal(false)}
                disabled={dangGui}
              >
                Hủy
              </Button>
              <Button
                onClick={xuLyGui}
                loading={dangGui}
                leftSection={<IconPlus size={16} />}
              >
                {mode === "create" ? "Thêm mới" : "Cập nhật"}
              </Button>
            </Group>
          </Stack>
        </Modal>
      </MantineProvider>
    </>
  );
}
